import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import { u2000OpenAppRouteInNewTab } from '../u2000Core/tools/u2000OpenAppRouteInNewTab';
import { doExportToTimesheet, luHandleApiError, type U3147ExportRequest, type U3147ExportResult } from './U3147Service';
import { NS } from './U3147';

export function useU3147ExportHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingExport, setPendingExport] = useState<U3147ExportRequest | null>(null);
  const [warnings, setWarnings] = useState<string[] | null>(null);
  const [lastTimesheetId, setLastTimesheetId] = useState<string | null>(null);

  const requestExport = useCallback((body: U3147ExportRequest) => {
    setPendingExport(body);
    setConfirmOpen(true);
  }, []);

  const cancelExport = useCallback(() => {
    setConfirmOpen(false);
    setPendingExport(null);
  }, []);

  const executeExport = useCallback(async () => {
    if (!pendingExport) return null;
    setBusy(true);
    setConfirmOpen(false);
    try {
      const response = await doExportToTimesheet(axios, pendingExport);
      const raw = response.data?.result ?? response.data;
      const result = raw as U3147ExportResult;
      setLastTimesheetId(result.timesheetId ?? null);
      if (result.warnings?.length) {
        setWarnings(result.warnings);
      } else {
        toast.success(
          t('msgExportSuccess', {
            ns: NS,
            count: result.exportedSessionCount ?? 0,
            defaultValue: 'Export réussi ({{count}} plage(s)).',
          }),
        );
      }
      return result;
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      return null;
    } finally {
      setBusy(false);
      setPendingExport(null);
    }
  }, [axios, pendingExport, t, toast]);

  const openTimesheetInNewTab = useCallback((timesheetId: string) => {
    if (!timesheetId?.trim()) return;
    u2000OpenAppRouteInNewTab(`/U3129/${timesheetId.trim()}`);
  }, []);

  const clearWarnings = useCallback(() => setWarnings(null), []);

  return {
    busy,
    confirmOpen,
    pendingExport,
    warnings,
    lastTimesheetId,
    requestExport,
    cancelExport,
    executeExport,
    openTimesheetInNewTab,
    clearWarnings,
  };
}
